'use client';

import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { AuthContext } from '@/app/components/Authentication';
import { pathForNextNavigation } from '@/lib/appBasePath';
import { listNotifications, markNotificationRead } from '@/services/notificationApi';
import type { NotificationResponse } from '@/services/notificationApi';

import type { NotificationItemProps } from './notification-item';
import { useNotificationSocket } from './useNotificationSocket';

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function toCategoryLabel(sourceService: string): string {
  const words = sourceService
    .replace(/[-_]?(service|svc)$/i, '')
    .split(/[-_\s]+/)
    .filter(Boolean);
  if (!words.length) return 'Platform';
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
}

// The notification service's `deep_link` doesn't always match this app's actual route structure.
const DEEP_LINK_PATH_REWRITES: [from: string, to: string][] = [['/wallet', '/account/wallet']];

function normalizeDeepLink(deepLink: string): string {
  const path = pathForNextNavigation(deepLink);
  for (const [from, to] of DEEP_LINK_PATH_REWRITES) {
    if (path === from) return to;
    if (path.startsWith(`${from}/`)) return `${to}${path.slice(from.length)}`;
  }
  return path;
}

function toNotificationItem(notification: NotificationResponse): NotificationItemProps {
  return {
    id: notification.id,
    type: 'order',
    title: `<p><strong>${escapeHtml(notification.title)}</strong></p><p>${escapeHtml(notification.message)}</p>`,
    category: toCategoryLabel(notification.source_service),
    isUnRead: !notification.is_read,
    avatarUrl: null,
    createdAt: notification.created_at,
    ...(notification.deep_link ? { href: normalizeDeepLink(notification.deep_link) } : {}),
  };
}

const POLL_INTERVAL_MS = 5_000;
const MAX_POLL_INTERVAL_MS = 60_000;
// After this many consecutive failures, stop auto-polling rather than
// hammering an endpoint that's down — the bell just goes quiet/degraded
// until the tab regains visibility (see the polling effect below).
const MAX_CONSECUTIVE_FAILURES = 5;

export function useNotifications() {
  const auth = useContext(AuthContext);
  const [notifications, setNotifications] = useState<NotificationItemProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [degraded, setDegraded] = useState(false);
  const consecutiveFailuresRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    if (!auth?.isAuthenticated) return;
    setLoading(true);
    try {
      const data = await listNotifications(auth.token, { limit: 50 });
      setNotifications(data.notifications.map(toNotificationItem));
      consecutiveFailuresRef.current = 0;
      setDegraded(false);
    } catch (error) {
      console.error('Failed to load notifications', error);
      consecutiveFailuresRef.current += 1;
      if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
        setDegraded(true);
      }
    } finally {
      setLoading(false);
    }
  }, [auth?.isAuthenticated, auth?.token]);

  // Initial + on-auth-change history load. This is the only way to get
  // history/unread count on load — the socket below only carries new events
  // going forward.
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handlePush = useCallback((notification: NotificationResponse) => {
    setNotifications((prev) => [toNotificationItem(notification), ...prev]);
  }, []);

  const socketStatus = useNotificationSocket(!!auth?.isAuthenticated, handlePush);

  // Polling is now a fallback, not the primary channel — it only runs once
  // the WebSocket has failed to connect / exhausted its reconnect attempts
  // (see useNotificationSocket). Still paused while the tab is hidden.
  //
  // Each poll failure backs the interval off exponentially (same shape as
  // the socket's reconnect backoff), and after MAX_CONSECUTIVE_FAILURES the
  // circuit opens — polling stops entirely instead of retrying forever
  // against a dead endpoint. Bringing the tab back into focus closes the
  // circuit and gives it one more try.
  useEffect(() => {
    if (!auth?.isAuthenticated || socketStatus !== 'failed') return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const clearPending = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const scheduleNext = () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) return;
      const delay = Math.min(
        POLL_INTERVAL_MS * 2 ** consecutiveFailuresRef.current,
        MAX_POLL_INTERVAL_MS
      );
      timeoutId = setTimeout(async () => {
        await fetchNotifications();
        scheduleNext();
      }, delay);
    };

    const handleVisibilityChange = () => {
      clearPending();
      if (document.visibilityState === 'visible') {
        // Tab regained focus: give the endpoint one more chance even if the
        // circuit had opened, rather than staying degraded forever.
        consecutiveFailuresRef.current = 0;
        fetchNotifications().then(scheduleNext);
      }
    };

    scheduleNext();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      clearPending();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [auth?.isAuthenticated, socketStatus, fetchNotifications]);

  const markAsRead = useCallback(
    (id: string) => {
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isUnRead: false } : n)));
      markNotificationRead(auth?.token, id).catch((error) => {
        console.error('Failed to mark notification as read', error);
      });
    },
    [auth?.token]
  );

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => {
      const unreadIds = prev.filter((n) => n.isUnRead).map((n) => n.id);
      unreadIds.forEach((id) => {
        markNotificationRead(auth?.token, id).catch((error) => {
          console.error('Failed to mark notification as read', error);
        });
      });
      return prev.map((n) => ({ ...n, isUnRead: false }));
    });
  }, [auth?.token]);

  return { notifications, loading, degraded, markAsRead, markAllAsRead };
}
